import React, { memo } from 'react';
import { Card } from '~/components/gluestack/card';
import { Skeleton } from '@/components/gluestack/skeleton';
import { EventBasic } from '@/models/Event';
import { Image } from 'expo-image';
import { Text } from '@/components/gluestack/text';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '~/components/gluestack/hstack';
import { Box } from '~/components/gluestack/box';
import { TouchableOpacity } from 'react-native';
import { Link } from 'expo-router';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { Ticket02Icon } from '@hugeicons-pro/core-solid-rounded';
import dayjs from '@/i18n/dayjs-config';
import { SafeBlurView as BlurView } from '~/components/ui/SafeBlurView';
import { CardLocationRow } from '~/components/ui/CardLocationRow';
interface EventCardProps {
  event?: EventBasic;
  /**
   * Compact variant intended for narrow containers (e.g. the feed right panel).
   * Reserves 2 lines for the event name and hides the distance from the location row.
   */
  compact?: boolean;
  /**
   * Optional class names appended to the inner Card (e.g. to override the background).
   */
  className?: string;
}

function formatEventDate(event: EventBasic): string {
  const date = dayjs(event.StartDate);
  const isCurrentYear = date.year() === dayjs().year();

  if (isCurrentYear) {
    // LL gives full month name; strip the year (and locale-specific connectors like "de" in es/pt)
    return date.format('LL').replace(/,?\s*(de\s+)?\d{4}/, '').trim();
  }

  return date.format('LL');
}

const EventCardComponent = ({ event, compact = false, className }: EventCardProps) => {
  if (!event) {
    return <EventCardSkeleton />;
  }

  const dateDisplay = formatEventDate(event);

  const cardContent = (
    <Card
      size="sm"
      className={`p-0 web:transition-transform web:duration-200 web:ease-in-out web:hover:scale-105 ${className ?? ''}`}>
      <Box className="relative bg-background-200 rounded-lg">
        <Image
          source={{ uri: event.IconUrl }}
          contentFit="cover"
          className="aspect-square w-full rounded-lg"
          recyclingKey={event.IconUrl}
          transition={200}
        />
        {event.IsAttending && (
          <HStack className="absolute bottom-2 left-0 right-0 w-full items-center justify-center">
            <Box className="size-8 overflow-hidden rounded-full">
              <BlurView
                intensity={80}
                tint="dark"
                className="h-full w-full items-center justify-center"
                blurMethod="none">
                <HugeIconsWrapper icon={Ticket02Icon} size={18} className="text-primary-500" />
              </BlurView>
            </Box>
          </HStack>
        )}
      </Box>
      <VStack className="items-center gap-1 px-2 pb-3 pt-2">
        {compact ? (
          <Box className="h-10 w-full items-center justify-center">
            <Text
              bold
              size="sm"
              numberOfLines={2}
              className="web:w-full web:text-center web:line-clamp-2 web:px-2 text-center leading-5">
              {event.Name}
            </Text>
          </Box>
        ) : (
          <Text bold isTruncated className="web:block web:w-full web:text-center">
            {event.Name}
          </Text>
        )}
        <Text size="sm" isTruncated className="max-w-full">
          {dateDisplay}
        </Text>
        <CardLocationRow
          placeName={event.PlaceNameShort}
          placeNameFull={event.PlaceNameCleaned}
          countryCode={event.CountryCode}
          distanceKm={compact ? null : event.DistanceKm}
        />
      </VStack>
    </Card>
  );

  return (
    <Link href={`/events/${event.Code}` as any} asChild>
      <TouchableOpacity activeOpacity={0.8}>
        {cardContent}
      </TouchableOpacity>
    </Link>
  );
};

export const EventCard = memo(EventCardComponent);

export function EventCardSkeleton() {
  return (
    <Card size="sm" className="p-0">
      <Box className="relative aspect-square w-full">
        <Skeleton variant="rounded" className="absolute inset-0 h-full w-full" />
      </Box>
      <VStack className="items-center gap-2 p-4">
        <Skeleton variant="rounded" className="h-5 w-24" />
        <Skeleton variant="rounded" className="h-3 w-32" />
        <Skeleton variant="rounded" className="h-3 w-28" />
      </VStack>
    </Card>
  );
}
